/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
export type AttributeInput = {
  key: string;
  value: string;
};

export type CartAddressInput = {
  copyFromCustomerAddressId?: string | number | null | undefined;
  deliveryAddress?: CartDeliveryAddressInput | null | undefined;
};

export type CartBuyerIdentityInput = {
  companyLocationId?: string | number | null | undefined;
  countryCode?: CountryCode | null | undefined;
  customerAccessToken?: string | null | undefined;
  email?: string | null | undefined;
  phone?: string | null | undefined;
  preferences?: CartPreferencesInput | null | undefined;
};

export type CartDeliveryAddressInput = {
  address1?: string | null | undefined;
  address2?: string | null | undefined;
  city?: string | null | undefined;
  company?: string | null | undefined;
  countryCode?: CountryCode | null | undefined;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  phone?: string | null | undefined;
  provinceCode?: string | null | undefined;
  zip?: string | null | undefined;
};

export type CartDeliveryCoordinatesPreferenceInput = {
  countryCode: CountryCode;
  latitude: number;
  longitude: number;
};

export type CartDeliveryInput = {
  addresses?: Array<CartSelectableAddressInput> | null | undefined;
};

export type CartDeliveryPreferenceInput = {
  coordinates?: CartDeliveryCoordinatesPreferenceInput | null | undefined;
  deliveryMethod?: Array<PreferenceDeliveryMethodType> | null | undefined;
  pickupHandle?: Array<string> | null | undefined;
};

export type CartErrorCode =
  | 'ADDRESS_FIELD_CONTAINS_EMOJIS'
  | 'ADDRESS_FIELD_CONTAINS_HTML_TAGS'
  | 'ADDRESS_FIELD_CONTAINS_URL'
  | 'ADDRESS_FIELD_DOES_NOT_MATCH_EXPECTED_PATTERN'
  | 'ADDRESS_FIELD_IS_REQUIRED'
  | 'ADDRESS_FIELD_IS_TOO_LONG'
  | 'BUNDLES_AND_ADDONS_CANNOT_BE_MIXED'
  | 'BUYER_CANNOT_PURCHASE_FOR_COMPANY_LOCATION'
  | 'CART_TOO_LARGE'
  | 'GIFT_CARD_RECIPIENT_INVALID'
  | 'INVALID'
  | 'INVALID_COMPANY_LOCATION'
  | 'INVALID_DELIVERY_ADDRESS_ID'
  | 'INVALID_DELIVERY_GROUP'
  | 'INVALID_DELIVERY_OPTION'
  | 'INVALID_INCREMENT'
  | 'INVALID_MERCHANDISE_LINE'
  | 'INVALID_METAFIELDS'
  | 'INVALID_PAYMENT'
  | 'INVALID_PAYMENT_DEFERRED_PAYMENT_REQUIRED'
  | 'INVALID_PAYMENT_EMPTY_CART'
  | 'INVALID_ZIP_CODE_FOR_COUNTRY'
  | 'INVALID_ZIP_CODE_FOR_PROVINCE'
  | 'LESS_THAN'
  | 'MAXIMUM_EXCEEDED'
  | 'MERCHANDISE_LINE_TRANSFORMERS_RUN_ERROR'
  | 'MERCHANDISE_NOT_APPLICABLE'
  | 'MINIMUM_NOT_MET'
  | 'MISSING_CUSTOMER_ACCESS_TOKEN'
  | 'MISSING_DISCOUNT_CODE'
  | 'MISSING_NOTE'
  | 'NOTE_TOO_LONG'
  | 'ONLY_ONE_DELIVERY_ADDRESS_CAN_BE_SELECTED'
  | 'PARENT_LINE_INVALID_REFERENCE'
  | 'PARENT_LINE_NESTING_TOO_DEEP'
  | 'PARENT_LINE_NOT_FOUND'
  | 'PARENT_LINE_OPERATION_BLOCKED'
  | 'PAYMENTS_CREDIT_CARD_BASE_EXPIRED'
  | 'PAYMENTS_CREDIT_CARD_BASE_GATEWAY_NOT_SUPPORTED'
  | 'PAYMENTS_CREDIT_CARD_GENERIC'
  | 'PAYMENTS_CREDIT_CARD_MONTH_INCLUSION'
  | 'PAYMENTS_CREDIT_CARD_NUMBER_INVALID'
  | 'PAYMENTS_CREDIT_CARD_NUMBER_INVALID_FORMAT'
  | 'PAYMENTS_CREDIT_CARD_VERIFICATION_VALUE_BLANK'
  | 'PAYMENTS_CREDIT_CARD_VERIFICATION_VALUE_INVALID_FOR_CARD_TYPE'
  | 'PAYMENTS_CREDIT_CARD_YEAR_EXPIRED'
  | 'PAYMENTS_CREDIT_CARD_YEAR_INVALID_EXPIRY_YEAR'
  | 'PAYMENT_METHOD_NOT_APPLICABLE'
  | 'PAYMENT_METHOD_NOT_SUPPORTED'
  | 'PENDING_DELIVERY_GROUPS'
  | 'PROVINCE_NOT_FOUND'
  | 'SELLING_PLAN_NOT_APPLICABLE'
  | 'SERVICE_UNAVAILABLE'
  | 'TOO_MANY_DELIVERY_ADDRESSES'
  | 'UNSPECIFIED_ADDRESS_ERROR'
  | 'VALIDATION_CUSTOM'
  | 'VARIANT_REQUIRES_SELLING_PLAN'
  | 'ZIP_CODE_NOT_SUPPORTED';

export type CartInput = {
  attributes?: Array<AttributeInput> | null | undefined;
  buyerIdentity?: CartBuyerIdentityInput | null | undefined;
  delivery?: CartDeliveryInput | null | undefined;
  discountCodes?: Array<string> | null | undefined;
  giftCardCodes?: Array<string> | null | undefined;
  lines?: Array<CartLineInput> | null | undefined;
  metafields?: Array<CartInputMetafieldInput> | null | undefined;
  note?: string | null | undefined;
  sourceName?: string | null | undefined;
};

export type CartInputMetafieldInput = {
  key: string;
  type: string;
  value: string;
};

export type CartLineInput = {
  attributes?: Array<AttributeInput> | null | undefined;
  merchandiseId: string | number;
  parent?: CartLineParentInput | null | undefined;
  quantity?: number | null | undefined;
  sellingPlanId?: string | number | null | undefined;
};

export type CartLineParentInput = {
  lineId?: string | number | null | undefined;
  merchandiseId?: string | number | null | undefined;
};

export type CartLineUpdateInput = {
  attributes?: Array<AttributeInput> | null | undefined;
  id?: string | number | null | undefined;
  merchandiseId?: string | number | null | undefined;
  quantity?: number | null | undefined;
  sellingPlanId?: string | number | null | undefined;
  viewKey?: string | null | undefined;
};

export type CartPreferencesInput = {
  delivery?: CartDeliveryPreferenceInput | null | undefined;
  wallet?: Array<string> | null | undefined;
};

export type CartSelectableAddressInput = {
  address: CartAddressInput;
  oneTimeUse?: boolean | null | undefined;
  selected?: boolean | null | undefined;
  validationStrategy?: DeliveryAddressValidationStrategy | null | undefined;
};

export type CartWarningCode =
  | 'DISCOUNT_CODE_NOT_HONOURED'
  | 'DISCOUNT_CURRENTLY_INACTIVE'
  | 'DISCOUNT_CUSTOMER_NOT_ELIGIBLE'
  | 'DISCOUNT_CUSTOMER_USAGE_LIMIT_REACHED'
  | 'DISCOUNT_ELIGIBLE_CUSTOMER_MISSING'
  | 'DISCOUNT_INCOMPATIBLE_PURCHASE_TYPE'
  | 'DISCOUNT_NOT_FOUND'
  | 'DISCOUNT_NO_ENTITLED_LINE_ITEMS'
  | 'DISCOUNT_NO_ENTITLED_SHIPPING_LINES'
  | 'DISCOUNT_PURCHASE_NOT_IN_RANGE'
  | 'DISCOUNT_QUANTITY_NOT_IN_RANGE'
  | 'DISCOUNT_USAGE_LIMIT_REACHED'
  | 'DUPLICATE_DELIVERY_ADDRESS'
  | 'MERCHANDISE_NOT_ENOUGH_STOCK'
  | 'MERCHANDISE_OUT_OF_STOCK'
  | 'MERCHANDISE_SELLING_PLAN_NOT_APPLICABLE_ON_COMPANY_LOCATION'
  | 'PAYMENTS_GIFT_CARDS_UNAVAILABLE'
  | 'PRODUCT_UNAVAILABLE_IN_BUYER_LOCATION';

export type CountryCode =
  | 'AC'
  | 'AD'
  | 'AE'
  | 'AF'
  | 'AG'
  | 'AI'
  | 'AL'
  | 'AM'
  | 'AN'
  | 'AO'
  | 'AR'
  | 'AT'
  | 'AU'
  | 'AW'
  | 'AX'
  | 'AZ'
  | 'BA'
  | 'BB'
  | 'BD'
  | 'BE'
  | 'BF'
  | 'BG'
  | 'BH'
  | 'BI'
  | 'BJ'
  | 'BL'
  | 'BM'
  | 'BN'
  | 'BO'
  | 'BQ'
  | 'BR'
  | 'BS'
  | 'BT'
  | 'BV'
  | 'BW'
  | 'BY'
  | 'BZ'
  | 'CA'
  | 'CC'
  | 'CD'
  | 'CF'
  | 'CG'
  | 'CH'
  | 'CI'
  | 'CK'
  | 'CL'
  | 'CM'
  | 'CN'
  | 'CO'
  | 'CR'
  | 'CU'
  | 'CV'
  | 'CW'
  | 'CX'
  | 'CY'
  | 'CZ'
  | 'DE'
  | 'DJ'
  | 'DK'
  | 'DM'
  | 'DO'
  | 'DZ'
  | 'EC'
  | 'EE'
  | 'EG'
  | 'EH'
  | 'ER'
  | 'ES'
  | 'ET'
  | 'FI'
  | 'FJ'
  | 'FK'
  | 'FO'
  | 'FR'
  | 'GA'
  | 'GB'
  | 'GD'
  | 'GE'
  | 'GF'
  | 'GG'
  | 'GH'
  | 'GI'
  | 'GL'
  | 'GM'
  | 'GN'
  | 'GP'
  | 'GQ'
  | 'GR'
  | 'GS'
  | 'GT'
  | 'GW'
  | 'GY'
  | 'HK'
  | 'HM'
  | 'HN'
  | 'HR'
  | 'HT'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IL'
  | 'IM'
  | 'IN'
  | 'IO'
  | 'IQ'
  | 'IR'
  | 'IS'
  | 'IT'
  | 'JE'
  | 'JM'
  | 'JO'
  | 'JP'
  | 'KE'
  | 'KG'
  | 'KH'
  | 'KI'
  | 'KM'
  | 'KN'
  | 'KP'
  | 'KR'
  | 'KW'
  | 'KY'
  | 'KZ'
  | 'LA'
  | 'LB'
  | 'LC'
  | 'LI'
  | 'LK'
  | 'LR'
  | 'LS'
  | 'LT'
  | 'LU'
  | 'LV'
  | 'LY'
  | 'MA'
  | 'MC'
  | 'MD'
  | 'ME'
  | 'MF'
  | 'MG'
  | 'MK'
  | 'ML'
  | 'MM'
  | 'MN'
  | 'MO'
  | 'MQ'
  | 'MR'
  | 'MS'
  | 'MT'
  | 'MU'
  | 'MV'
  | 'MW'
  | 'MX'
  | 'MY'
  | 'MZ'
  | 'NA'
  | 'NC'
  | 'NE'
  | 'NF'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NP'
  | 'NR'
  | 'NU'
  | 'NZ'
  | 'OM'
  | 'PA'
  | 'PE'
  | 'PF'
  | 'PG'
  | 'PH'
  | 'PK'
  | 'PL'
  | 'PM'
  | 'PN'
  | 'PS'
  | 'PT'
  | 'PY'
  | 'QA'
  | 'RE'
  | 'RO'
  | 'RS'
  | 'RU'
  | 'RW'
  | 'SA'
  | 'SB'
  | 'SC'
  | 'SD'
  | 'SE'
  | 'SG'
  | 'SH'
  | 'SI'
  | 'SJ'
  | 'SK'
  | 'SL'
  | 'SM'
  | 'SN'
  | 'SO'
  | 'SR'
  | 'SS'
  | 'ST'
  | 'SV'
  | 'SX'
  | 'SY'
  | 'SZ'
  | 'TA'
  | 'TC'
  | 'TD'
  | 'TF'
  | 'TG'
  | 'TH'
  | 'TJ'
  | 'TK'
  | 'TL'
  | 'TM'
  | 'TN'
  | 'TO'
  | 'TR'
  | 'TT'
  | 'TV'
  | 'TW'
  | 'TZ'
  | 'UA'
  | 'UG'
  | 'UM'
  | 'US'
  | 'UY'
  | 'UZ'
  | 'VA'
  | 'VC'
  | 'VE'
  | 'VG'
  | 'VN'
  | 'VU'
  | 'WF'
  | 'WS'
  | 'XK'
  | 'YE'
  | 'YT'
  | 'ZA'
  | 'ZM'
  | 'ZW'
  | 'ZZ';

export type CurrencyCode =
  | 'AED'
  | 'AFN'
  | 'ALL'
  | 'AMD'
  | 'ANG'
  | 'AOA'
  | 'ARS'
  | 'AUD'
  | 'AWG'
  | 'AZN'
  | 'BAM'
  | 'BBD'
  | 'BDT'
  | 'BGN'
  | 'BHD'
  | 'BIF'
  | 'BMD'
  | 'BND'
  | 'BOB'
  | 'BRL'
  | 'BSD'
  | 'BTN'
  | 'BWP'
  | 'BYN'
  | 'BYR'
  | 'BZD'
  | 'CAD'
  | 'CDF'
  | 'CHF'
  | 'CLP'
  | 'CNY'
  | 'COP'
  | 'CRC'
  | 'CVE'
  | 'CZK'
  | 'DJF'
  | 'DKK'
  | 'DOP'
  | 'DZD'
  | 'EGP'
  | 'ERN'
  | 'ETB'
  | 'EUR'
  | 'FJD'
  | 'FKP'
  | 'GBP'
  | 'GEL'
  | 'GHS'
  | 'GIP'
  | 'GMD'
  | 'GNF'
  | 'GTQ'
  | 'GYD'
  | 'HKD'
  | 'HNL'
  | 'HRK'
  | 'HTG'
  | 'HUF'
  | 'IDR'
  | 'ILS'
  | 'INR'
  | 'IQD'
  | 'IRR'
  | 'ISK'
  | 'JEP'
  | 'JMD'
  | 'JOD'
  | 'JPY'
  | 'KES'
  | 'KGS'
  | 'KHR'
  | 'KID'
  | 'KMF'
  | 'KRW'
  | 'KWD'
  | 'KYD'
  | 'KZT'
  | 'LAK'
  | 'LBP'
  | 'LKR'
  | 'LRD'
  | 'LSL'
  | 'LTL'
  | 'LVL'
  | 'LYD'
  | 'MAD'
  | 'MDL'
  | 'MGA'
  | 'MKD'
  | 'MMK'
  | 'MNT'
  | 'MOP'
  | 'MRU'
  | 'MUR'
  | 'MVR'
  | 'MWK'
  | 'MXN'
  | 'MYR'
  | 'MZN'
  | 'NAD'
  | 'NGN'
  | 'NIO'
  | 'NOK'
  | 'NPR'
  | 'NZD'
  | 'OMR'
  | 'PAB'
  | 'PEN'
  | 'PGK'
  | 'PHP'
  | 'PKR'
  | 'PLN'
  | 'PYG'
  | 'QAR'
  | 'RON'
  | 'RSD'
  | 'RUB'
  | 'RWF'
  | 'SAR'
  | 'SBD'
  | 'SCR'
  | 'SDG'
  | 'SEK'
  | 'SGD'
  | 'SHP'
  | 'SLL'
  | 'SOS'
  | 'SRD'
  | 'SSP'
  | 'STD'
  | 'STN'
  | 'SYP'
  | 'SZL'
  | 'THB'
  | 'TJS'
  | 'TMT'
  | 'TND'
  | 'TOP'
  | 'TRY'
  | 'TTD'
  | 'TWD'
  | 'TZS'
  | 'UAH'
  | 'UGX'
  | 'USD'
  | 'UYU'
  | 'UZS'
  | 'VED'
  | 'VEF'
  | 'VES'
  | 'VND'
  | 'VUV'
  | 'WST'
  | 'XAF'
  | 'XCD'
  | 'XCG'
  | 'XOF'
  | 'XPF'
  | 'XXX'
  | 'YER'
  | 'ZAR'
  | 'ZMW';

export type CustomerErrorCode =
  | 'ALREADY_ENABLED'
  | 'BAD_DOMAIN'
  | 'BLANK'
  | 'CONTAINS_HTML_TAGS'
  | 'CONTAINS_URL'
  | 'CUSTOMER_DISABLED'
  | 'INVALID'
  | 'INVALID_MULTIPASS_REQUEST'
  | 'NOT_FOUND'
  | 'PASSWORD_STARTS_OR_ENDS_WITH_WHITESPACE'
  | 'TAKEN'
  | 'TOKEN_INVALID'
  | 'TOO_LONG'
  | 'TOO_SHORT'
  | 'UNIDENTIFIED_CUSTOMER';

export type DeliveryAddressValidationStrategy =
  | 'COUNTRY_CODE_ONLY'
  | 'STRICT';

export type LanguageCode =
  | 'AF'
  | 'AK'
  | 'AM'
  | 'AR'
  | 'AS'
  | 'AZ'
  | 'BE'
  | 'BG'
  | 'BM'
  | 'BN'
  | 'BO'
  | 'BR'
  | 'BS'
  | 'CA'
  | 'CE'
  | 'CKB'
  | 'CS'
  | 'CU'
  | 'CY'
  | 'DA'
  | 'DE'
  | 'DZ'
  | 'EE'
  | 'EL'
  | 'EN'
  | 'EO'
  | 'ES'
  | 'ET'
  | 'EU'
  | 'FA'
  | 'FF'
  | 'FI'
  | 'FIL'
  | 'FO'
  | 'FR'
  | 'FY'
  | 'GA'
  | 'GD'
  | 'GL'
  | 'GU'
  | 'GV'
  | 'HA'
  | 'HE'
  | 'HI'
  | 'HR'
  | 'HU'
  | 'HY'
  | 'IA'
  | 'ID'
  | 'IG'
  | 'II'
  | 'IS'
  | 'IT'
  | 'JA'
  | 'JV'
  | 'KA'
  | 'KI'
  | 'KK'
  | 'KL'
  | 'KM'
  | 'KN'
  | 'KO'
  | 'KS'
  | 'KU'
  | 'KW'
  | 'KY'
  | 'LA'
  | 'LB'
  | 'LG'
  | 'LN'
  | 'LO'
  | 'LT'
  | 'LU'
  | 'LV'
  | 'MG'
  | 'MI'
  | 'MK'
  | 'ML'
  | 'MN'
  | 'MO'
  | 'MR'
  | 'MS'
  | 'MT'
  | 'MY'
  | 'NB'
  | 'ND'
  | 'NE'
  | 'NL'
  | 'NN'
  | 'NO'
  | 'OM'
  | 'OR'
  | 'OS'
  | 'PA'
  | 'PL'
  | 'PS'
  | 'PT'
  | 'PT_BR'
  | 'PT_PT'
  | 'QU'
  | 'RM'
  | 'RN'
  | 'RO'
  | 'RU'
  | 'RW'
  | 'SA'
  | 'SC'
  | 'SD'
  | 'SE'
  | 'SG'
  | 'SH'
  | 'SI'
  | 'SK'
  | 'SL'
  | 'SN'
  | 'SO'
  | 'SQ'
  | 'SR'
  | 'SU'
  | 'SV'
  | 'SW'
  | 'TA'
  | 'TE'
  | 'TG'
  | 'TH'
  | 'TI'
  | 'TK'
  | 'TO'
  | 'TR'
  | 'TT'
  | 'UG'
  | 'UK'
  | 'UR'
  | 'UZ'
  | 'VI'
  | 'VO'
  | 'WO'
  | 'XH'
  | 'YI'
  | 'YO'
  | 'ZH'
  | 'ZH_CN'
  | 'ZH_TW'
  | 'ZU';

export type PreferenceDeliveryMethodType =
  | 'PICKUP_POINT'
  | 'PICK_UP'
  | 'SHIPPING';

export type SellingPlanInterval =
  | 'DAY'
  | 'MONTH'
  | 'WEEK'
  | 'YEAR';

export type VisitorConsent = {
  analytics?: boolean | null | undefined;
  marketing?: boolean | null | undefined;
  preferences?: boolean | null | undefined;
  saleOfData?: boolean | null | undefined;
};

export type AddCartLinesMutationVariables = Exact<{
  cartId: string | number;
  lines: Array<CartLineInput> | CartLineInput;
  country: CountryCode;
  language: LanguageCode;
  visitorConsent?: VisitorConsent | null | undefined;
}>;


export type AddCartLinesMutation = { cartLinesAdd: { cart: { id: string, checkoutUrl: string, totalQuantity: number, attributes: Array<{ key: string, value: string | null }>, buyerIdentity: { customer: { id: string } | null }, cost: { subtotalAmount: { amount: string, currencyCode: CurrencyCode } }, lines: { edges: Array<{ node:
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
           }> } } | null, userErrors: Array<{ code: CartErrorCode | null, field: Array<string> | null, message: string }>, warnings: Array<{ code: CartWarningCode, message: string, target: string }> } | null };

export type CartFieldsFragment = { id: string, checkoutUrl: string, totalQuantity: number, attributes: Array<{ key: string, value: string | null }>, buyerIdentity: { customer: { id: string } | null }, cost: { subtotalAmount: { amount: string, currencyCode: CurrencyCode } }, lines: { edges: Array<{ node:
        | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
        | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
       }> } };

export type CartUserErrorFieldsFragment = { code: CartErrorCode | null, field: Array<string> | null, message: string };

export type CartWarningFieldsFragment = { code: CartWarningCode, message: string, target: string };

export type CreateCartMutationVariables = Exact<{
  input: CartInput;
  country: CountryCode;
  language: LanguageCode;
  visitorConsent?: VisitorConsent | null | undefined;
}>;


export type CreateCartMutation = { cartCreate: { cart: { id: string, checkoutUrl: string, totalQuantity: number, attributes: Array<{ key: string, value: string | null }>, buyerIdentity: { customer: { id: string } | null }, cost: { subtotalAmount: { amount: string, currencyCode: CurrencyCode } }, lines: { edges: Array<{ node:
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
           }> } } | null, userErrors: Array<{ code: CartErrorCode | null, field: Array<string> | null, message: string }> } | null };

export type GetCartQueryVariables = Exact<{
  cartId: string | number;
  country: CountryCode;
  language: LanguageCode;
  visitorConsent?: VisitorConsent | null | undefined;
}>;


export type GetCartQuery = { cart: { id: string, checkoutUrl: string, totalQuantity: number, attributes: Array<{ key: string, value: string | null }>, buyerIdentity: { customer: { id: string } | null }, cost: { subtotalAmount: { amount: string, currencyCode: CurrencyCode } }, lines: { edges: Array<{ node:
          | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
          | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
         }> } } | null };

export type RemoveCartLinesMutationVariables = Exact<{
  cartId: string | number;
  lineIds: Array<string | number> | string | number;
  country: CountryCode;
  language: LanguageCode;
  visitorConsent?: VisitorConsent | null | undefined;
}>;


export type RemoveCartLinesMutation = { cartLinesRemove: { cart: { id: string, checkoutUrl: string, totalQuantity: number, attributes: Array<{ key: string, value: string | null }>, buyerIdentity: { customer: { id: string } | null }, cost: { subtotalAmount: { amount: string, currencyCode: CurrencyCode } }, lines: { edges: Array<{ node:
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
           }> } } | null, userErrors: Array<{ code: CartErrorCode | null, field: Array<string> | null, message: string }>, warnings: Array<{ code: CartWarningCode, message: string, target: string }> } | null };

export type UpdateCartAttributesMutationVariables = Exact<{
  attributes: Array<AttributeInput> | AttributeInput;
  cartId: string | number;
  country: CountryCode;
  language: LanguageCode;
  visitorConsent?: VisitorConsent | null | undefined;
}>;


export type UpdateCartAttributesMutation = { cartAttributesUpdate: { cart: { id: string, checkoutUrl: string, totalQuantity: number, attributes: Array<{ key: string, value: string | null }>, buyerIdentity: { customer: { id: string } | null }, cost: { subtotalAmount: { amount: string, currencyCode: CurrencyCode } }, lines: { edges: Array<{ node:
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
           }> } } | null, userErrors: Array<{ code: CartErrorCode | null, field: Array<string> | null, message: string }>, warnings: Array<{ code: CartWarningCode, message: string, target: string }> } | null };

export type UpdateCartBuyerIdentityMutationVariables = Exact<{
  buyerIdentity: CartBuyerIdentityInput;
  cartId: string | number;
  country: CountryCode;
  language: LanguageCode;
  visitorConsent?: VisitorConsent | null | undefined;
}>;


export type UpdateCartBuyerIdentityMutation = { cartBuyerIdentityUpdate: { cart: { id: string, checkoutUrl: string, totalQuantity: number, attributes: Array<{ key: string, value: string | null }>, buyerIdentity: { customer: { id: string } | null }, cost: { subtotalAmount: { amount: string, currencyCode: CurrencyCode } }, lines: { edges: Array<{ node:
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
           }> } } | null, userErrors: Array<{ code: CartErrorCode | null, field: Array<string> | null, message: string }>, warnings: Array<{ code: CartWarningCode, message: string, target: string }> } | null };

export type UpdateCartLinesMutationVariables = Exact<{
  cartId: string | number;
  lines: Array<CartLineUpdateInput> | CartLineUpdateInput;
  country: CountryCode;
  language: LanguageCode;
  visitorConsent?: VisitorConsent | null | undefined;
}>;


export type UpdateCartLinesMutation = { cartLinesUpdate: { cart: { id: string, checkoutUrl: string, totalQuantity: number, attributes: Array<{ key: string, value: string | null }>, buyerIdentity: { customer: { id: string } | null }, cost: { subtotalAmount: { amount: string, currencyCode: CurrencyCode } }, lines: { edges: Array<{ node:
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
            | { id: string, quantity: number, attributeAddedFrom: { value: string | null } | null, attributeClickedFrom: { value: string | null } | null, cost: { amountPerQuantity: { amount: string, currencyCode: CurrencyCode }, compareAtAmountPerQuantity: { amount: string, currencyCode: CurrencyCode } | null, totalAmount: { amount: string, currencyCode: CurrencyCode } }, merchandise: { availableForSale: boolean, id: string, requiresShipping: boolean, sku: string | null, title: string, image: { url: string } | null, metafieldInternalDisplayName: { value: string } | null, metafieldAnalyticsDimensions: { value: string } | null, metafieldBackordered: { value: string } | null, metafieldPreorder: { value: string } | null, price: { amount: string, currencyCode: CurrencyCode }, product: { id: string, handle: string, tags: Array<string>, title: string, metafieldOemPartnership: { value: string } | null } }, sellingPlanAllocation: { sellingPlan: { id: string, billingPolicy: { interval: SellingPlanInterval, intervalCount: number } | null } } | null }
           }> } } | null, userErrors: Array<{ code: CartErrorCode | null, field: Array<string> | null, message: string }>, warnings: Array<{ code: CartWarningCode, message: string, target: string }> } | null };

export type CreateCustomerAccessTokenWithMultipassMutationVariables = Exact<{
  multipassToken: string;
}>;


export type CreateCustomerAccessTokenWithMultipassMutation = { customerAccessTokenCreateWithMultipass: { customerAccessToken: { accessToken: string, expiresAt: string } | null, customerUserErrors: Array<{ code: CustomerErrorCode | null, field: Array<string> | null, message: string }> } | null };

export type CustomerAccessTokenFieldsFragment = { accessToken: string, expiresAt: string };

export type GetCustomerOrdersQueryVariables = Exact<{
  customerAccessToken: string;
}>;


export type GetCustomerOrdersQuery = { customer: { orders: { nodes: Array<{ name: string, processedAt: string, totalPrice: { amount: string, currencyCode: CurrencyCode }, lineItems: { nodes: Array<{ title: string, quantity: number, variant: { sku: string | null, image: { url: string } | null } | null }> } }> } } | null };

export const CartFieldsFragmentDoc = `
    fragment CartFields on Cart {
  id
  attributes {
    key
    value
  }
  buyerIdentity {
    customer {
      id
    }
  }
  checkoutUrl
  cost {
    subtotalAmount {
      amount
      currencyCode
    }
  }
  lines(first: 250) {
    edges {
      node {
        id
        attributeAddedFrom: attribute(key: "_added_from") {
          value
        }
        attributeClickedFrom: attribute(key: "_clicked_from") {
          value
        }
        cost {
          amountPerQuantity {
            amount
            currencyCode
          }
          compareAtAmountPerQuantity {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
        merchandise {
          ... on ProductVariant {
            availableForSale
            id
            image {
              url
            }
            metafieldInternalDisplayName: metafield(
              namespace: "ifixit"
              key: "internal_display_name"
            ) {
              value
            }
            metafieldAnalyticsDimensions: metafield(
              namespace: "ifixit"
              key: "analytics_dimensions"
            ) {
              value
            }
            metafieldBackordered: metafield(namespace: "ifixit", key: "backordered") {
              value
            }
            metafieldPreorder: metafield(namespace: "ifixit", key: "preorder") {
              value
            }
            price {
              amount
              currencyCode
            }
            product {
              id
              handle
              tags
              title
              metafieldOemPartnership: metafield(
                namespace: "ifixit"
                key: "oem_partnership_json"
              ) {
                value
              }
            }
            requiresShipping
            sku
            title
          }
        }
        quantity
        sellingPlanAllocation {
          sellingPlan {
            id
            billingPolicy {
              ... on SellingPlanRecurringBillingPolicy {
                interval
                intervalCount
              }
            }
          }
        }
      }
    }
  }
  totalQuantity
}
    `;
export const CartUserErrorFieldsFragmentDoc = `
    fragment CartUserErrorFields on CartUserError {
  code
  field
  message
}
    `;
export const CartWarningFieldsFragmentDoc = `
    fragment CartWarningFields on CartWarning {
  code
  message
  target
}
    `;
export const CustomerAccessTokenFieldsFragmentDoc = `
    fragment CustomerAccessTokenFields on CustomerAccessToken {
  accessToken
  expiresAt
}
    `;
export const AddCartLinesDocument = `
    mutation addCartLines($cartId: ID!, $lines: [CartLineInput!]!, $country: CountryCode!, $language: LanguageCode!, $visitorConsent: VisitorConsent) @inContext(country: $country, language: $language, visitorConsent: $visitorConsent) {
  cartLinesAdd(cartId: $cartId, lines: $lines) {
    cart {
      ...CartFields
    }
    userErrors {
      ...CartUserErrorFields
    }
    warnings {
      ...CartWarningFields
    }
  }
}
    ${CartFieldsFragmentDoc}
${CartUserErrorFieldsFragmentDoc}
${CartWarningFieldsFragmentDoc}`;
export const CreateCartDocument = `
    mutation createCart($input: CartInput!, $country: CountryCode!, $language: LanguageCode!, $visitorConsent: VisitorConsent) @inContext(country: $country, language: $language, visitorConsent: $visitorConsent) {
  cartCreate(input: $input) {
    cart {
      ...CartFields
    }
    userErrors {
      ...CartUserErrorFields
    }
  }
}
    ${CartFieldsFragmentDoc}
${CartUserErrorFieldsFragmentDoc}`;
export const GetCartDocument = `
    query getCart($cartId: ID!, $country: CountryCode!, $language: LanguageCode!, $visitorConsent: VisitorConsent) @inContext(country: $country, language: $language, visitorConsent: $visitorConsent) {
  cart(id: $cartId) {
    ...CartFields
  }
}
    ${CartFieldsFragmentDoc}`;
export const RemoveCartLinesDocument = `
    mutation removeCartLines($cartId: ID!, $lineIds: [ID!]!, $country: CountryCode!, $language: LanguageCode!, $visitorConsent: VisitorConsent) @inContext(country: $country, language: $language, visitorConsent: $visitorConsent) {
  cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
    cart {
      ...CartFields
    }
    userErrors {
      ...CartUserErrorFields
    }
    warnings {
      ...CartWarningFields
    }
  }
}
    ${CartFieldsFragmentDoc}
${CartUserErrorFieldsFragmentDoc}
${CartWarningFieldsFragmentDoc}`;
export const UpdateCartAttributesDocument = `
    mutation updateCartAttributes($attributes: [AttributeInput!]!, $cartId: ID!, $country: CountryCode!, $language: LanguageCode!, $visitorConsent: VisitorConsent) @inContext(country: $country, language: $language, visitorConsent: $visitorConsent) {
  cartAttributesUpdate(attributes: $attributes, cartId: $cartId) {
    cart {
      ...CartFields
    }
    userErrors {
      ...CartUserErrorFields
    }
    warnings {
      ...CartWarningFields
    }
  }
}
    ${CartFieldsFragmentDoc}
${CartUserErrorFieldsFragmentDoc}
${CartWarningFieldsFragmentDoc}`;
export const UpdateCartBuyerIdentityDocument = `
    mutation updateCartBuyerIdentity($buyerIdentity: CartBuyerIdentityInput!, $cartId: ID!, $country: CountryCode!, $language: LanguageCode!, $visitorConsent: VisitorConsent) @inContext(country: $country, language: $language, visitorConsent: $visitorConsent) {
  cartBuyerIdentityUpdate(buyerIdentity: $buyerIdentity, cartId: $cartId) {
    cart {
      ...CartFields
    }
    userErrors {
      ...CartUserErrorFields
    }
    warnings {
      ...CartWarningFields
    }
  }
}
    ${CartFieldsFragmentDoc}
${CartUserErrorFieldsFragmentDoc}
${CartWarningFieldsFragmentDoc}`;
export const UpdateCartLinesDocument = `
    mutation updateCartLines($cartId: ID!, $lines: [CartLineUpdateInput!]!, $country: CountryCode!, $language: LanguageCode!, $visitorConsent: VisitorConsent) @inContext(country: $country, language: $language, visitorConsent: $visitorConsent) {
  cartLinesUpdate(cartId: $cartId, lines: $lines) {
    cart {
      ...CartFields
    }
    userErrors {
      ...CartUserErrorFields
    }
    warnings {
      ...CartWarningFields
    }
  }
}
    ${CartFieldsFragmentDoc}
${CartUserErrorFieldsFragmentDoc}
${CartWarningFieldsFragmentDoc}`;
export const CreateCustomerAccessTokenWithMultipassDocument = `
    mutation createCustomerAccessTokenWithMultipass($multipassToken: String!) {
  customerAccessTokenCreateWithMultipass(multipassToken: $multipassToken) {
    customerAccessToken {
      ...CustomerAccessTokenFields
    }
    customerUserErrors {
      code
      field
      message
    }
  }
}
    ${CustomerAccessTokenFieldsFragmentDoc}`;
export const GetCustomerOrdersDocument = `
    query getCustomerOrders($customerAccessToken: String!) {
  customer(customerAccessToken: $customerAccessToken) {
    orders(first: 50, sortKey: PROCESSED_AT, reverse: true) {
      nodes {
        name
        processedAt
        totalPrice {
          amount
          currencyCode
        }
        lineItems(first: 100) {
          nodes {
            title
            quantity
            variant {
              sku
              image {
                url(transform: {maxHeight: 80})
              }
            }
          }
        }
      }
    }
  }
}
    `;
export type Requester<C = {}> = <R, V>(doc: string, vars?: V, options?: C) => Promise<R> | AsyncIterable<R>
export function getSdk<C>(requester: Requester<C>) {
  return {
    addCartLines(variables: AddCartLinesMutationVariables, options?: C): Promise<AddCartLinesMutation> {
      return requester<AddCartLinesMutation, AddCartLinesMutationVariables>(AddCartLinesDocument, variables, options) as Promise<AddCartLinesMutation>;
    },
    createCart(variables: CreateCartMutationVariables, options?: C): Promise<CreateCartMutation> {
      return requester<CreateCartMutation, CreateCartMutationVariables>(CreateCartDocument, variables, options) as Promise<CreateCartMutation>;
    },
    getCart(variables: GetCartQueryVariables, options?: C): Promise<GetCartQuery> {
      return requester<GetCartQuery, GetCartQueryVariables>(GetCartDocument, variables, options) as Promise<GetCartQuery>;
    },
    removeCartLines(variables: RemoveCartLinesMutationVariables, options?: C): Promise<RemoveCartLinesMutation> {
      return requester<RemoveCartLinesMutation, RemoveCartLinesMutationVariables>(RemoveCartLinesDocument, variables, options) as Promise<RemoveCartLinesMutation>;
    },
    updateCartAttributes(variables: UpdateCartAttributesMutationVariables, options?: C): Promise<UpdateCartAttributesMutation> {
      return requester<UpdateCartAttributesMutation, UpdateCartAttributesMutationVariables>(UpdateCartAttributesDocument, variables, options) as Promise<UpdateCartAttributesMutation>;
    },
    updateCartBuyerIdentity(variables: UpdateCartBuyerIdentityMutationVariables, options?: C): Promise<UpdateCartBuyerIdentityMutation> {
      return requester<UpdateCartBuyerIdentityMutation, UpdateCartBuyerIdentityMutationVariables>(UpdateCartBuyerIdentityDocument, variables, options) as Promise<UpdateCartBuyerIdentityMutation>;
    },
    updateCartLines(variables: UpdateCartLinesMutationVariables, options?: C): Promise<UpdateCartLinesMutation> {
      return requester<UpdateCartLinesMutation, UpdateCartLinesMutationVariables>(UpdateCartLinesDocument, variables, options) as Promise<UpdateCartLinesMutation>;
    },
    createCustomerAccessTokenWithMultipass(variables: CreateCustomerAccessTokenWithMultipassMutationVariables, options?: C): Promise<CreateCustomerAccessTokenWithMultipassMutation> {
      return requester<CreateCustomerAccessTokenWithMultipassMutation, CreateCustomerAccessTokenWithMultipassMutationVariables>(CreateCustomerAccessTokenWithMultipassDocument, variables, options) as Promise<CreateCustomerAccessTokenWithMultipassMutation>;
    },
    getCustomerOrders(variables: GetCustomerOrdersQueryVariables, options?: C): Promise<GetCustomerOrdersQuery> {
      return requester<GetCustomerOrdersQuery, GetCustomerOrdersQueryVariables>(GetCustomerOrdersDocument, variables, options) as Promise<GetCustomerOrdersQuery>;
    }
  };
}
export type Sdk = ReturnType<typeof getSdk>;